"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const models_1 = require("../models");
const utils_1 = require("../utils");
const getInitialChatList = () => {
    return {
        chats: [],
        hasNext: true,
        limit: 0,
        totalUnreadChatCount: 0,
        unreadChatCount: 0,
    };
};
class ChatsStorage {
    constructor(orgStorage, isFiltered) {
        this.patientChatList = getInitialChatList();
        this.nonPatientChatList = getInitialChatList();
        this.privateChatList = getInitialChatList();
        this.broadcastChatList = getInitialChatList();
        this.hiddenChatList = getInitialChatList();
        this.orgStorage = orgStorage;
        this.isFiltered = isFiltered;
    }
    reset() {
        this.patientChatList = getInitialChatList();
        this.nonPatientChatList = getInitialChatList();
        this.privateChatList = getInitialChatList();
        this.broadcastChatList = getInitialChatList();
        this.hiddenChatList = getInitialChatList();
    }
    resetChatListByType(groupType) {
        this.setChatListByType(groupType, getInitialChatList());
    }
    getChatListByType(groupType) {
        switch (groupType) {
            case models_1.GROUP_TYPE.PATIENT:
                return this.patientChatList;
            case models_1.GROUP_TYPE.NON_PATIENT:
                return this.nonPatientChatList;
            case models_1.GROUP_TYPE.PRIVATE:
                return this.privateChatList;
            case models_1.GROUP_TYPE.HIDDEN:
                return this.hiddenChatList;
            default:
                return this.broadcastChatList;
        }
    }
    setChatListByType(groupType, chatList) {
        const previousChatList = this.getChatListByType(groupType);
        const previousUnreadChatCount = previousChatList.unreadChatCount;
        switch (groupType) {
            case models_1.GROUP_TYPE.PATIENT:
                this.patientChatList = chatList;
                break;
            case models_1.GROUP_TYPE.NON_PATIENT:
                this.nonPatientChatList = chatList;
                break;
            case models_1.GROUP_TYPE.BROADCAST:
                this.broadcastChatList = chatList;
                break;
            case models_1.GROUP_TYPE.HIDDEN:
                this.hiddenChatList = chatList;
                break;
            default:
                this.privateChatList = chatList;
                break;
        }
        if (previousUnreadChatCount !== chatList.unreadChatCount) {
            console.warn(`[DEBUG unreadCount] setChatListByType | ${groupType} | ${previousUnreadChatCount} → ${chatList.unreadChatCount} | delta: ${chatList.unreadChatCount - previousUnreadChatCount} | totalChats: ${chatList.chats.length} | chatsWithUnread: ${chatList.chats.filter((c) => c.unreadCount > 0).length} | isFiltered: ${this.isFiltered}`, new Error().stack);
        }
        if (chatList.unreadChatCount < 0) {
            console.error(`NegativeUnreadCountException: Unread chat count cannot be negative | groupType: ${groupType} | unreadChatCount: ${chatList.unreadChatCount} | totalChats: ${chatList.chats.length} | chatsWithUnread: ${chatList.chats.filter((c) => c.unreadCount > 0).length} | isFiltered: ${this.isFiltered}`);
        }
        if (!this.isFiltered) {
            this.orgStorage.setMessageCounterByType(groupType, chatList.unreadChatCount);
        }
    }
    bringChatOnTop(groupType, chatId) {
        const chatList = this.getChatListByType(groupType);
        if (chatId) {
            const currentChat = chatList.chats.find(chat => chat.from === chatId);
            if (currentChat) {
                const restChats = chatList.chats.filter(chat => chat.from !== chatId);
                const updatedChatList = Object.assign(Object.assign({}, chatList), { chats: [currentChat, ...restChats] });
                this.setChatListByType(groupType, updatedChatList);
            }
        }
    }
    upsertChat(groupType, currentChat, shouldUpdateUnreadChatCount, shouldBringOnTop) {
        const chatList = this.getChatListByType(groupType);
        const currentChatId = currentChat.id;
        let updatedChats;
        if (shouldBringOnTop) {
            const restChats = currentChatId
                ? chatList.chats.filter(chat => chat.from !== currentChatId)
                : chatList.chats;
            updatedChats = [currentChat, ...restChats];
        }
        else {
            updatedChats = currentChatId
                ? chatList.chats.map(chat => chat.from === currentChatId ? currentChat : chat)
                : [...chatList.chats];
        }
        const newUnreadChatCount = shouldUpdateUnreadChatCount
            ? chatList.unreadChatCount + 1
            : chatList.unreadChatCount;
        if (shouldUpdateUnreadChatCount) {
            console.warn(`[DEBUG unreadCount] upsertChat INCREMENT | ${groupType} | chatId: ${currentChatId} | unreadCount: ${currentChat.unreadCount} | unreadChatCount: ${chatList.unreadChatCount} → ${newUnreadChatCount} | shouldBringOnTop: ${shouldBringOnTop} | isFiltered: ${this.isFiltered}`);
        }
        const updatedChatList = Object.assign(Object.assign({}, chatList), { chats: updatedChats, unreadChatCount: newUnreadChatCount });
        this.setChatListByType(groupType, updatedChatList);
    }
    updateChat(chatId, type, chatData, unreadCount) {
        const chatList = this.getChatListByType(type);
        const chat = chatList.chats.find(chat => chat.id === chatId || (chat === null || chat === void 0 ? void 0 : chat.user) === chatId);
        const existingUnreadCount = (chat === null || chat === void 0 ? void 0 : chat.unreadCount) || 0;
        // Calculate how the unread chat count should change
        // Only decrease the count when it transitions from having unread messages (>0) to none (0)
        // This helps prevent race conditions in concurrent updates
        const chatListCountDelta = existingUnreadCount > 0 && unreadCount === 0 ? 1 : 0;
        const newUnreadChatCount = chat
            ? chatList.unreadChatCount - chatListCountDelta
            : chatList.unreadChatCount;
        if (chatListCountDelta > 0) {
            console.warn(`[DEBUG unreadCount] updateChat DECREMENT | ${type} | chatId: ${chatId} | existingUnreadCount: ${existingUnreadCount} | newUnreadCount: ${unreadCount} | unreadChatCount: ${chatList.unreadChatCount} → ${newUnreadChatCount} | chatFound: ${!!chat} | isFiltered: ${this.isFiltered}`);
        }
        const updatedChatList = Object.assign(Object.assign({}, chatList), { chats: chatList.chats.map(chat => chatId === chat.id || chatId === (chat === null || chat === void 0 ? void 0 : chat.user)
                ? Object.assign(Object.assign({}, chat), chatData) : chat), unreadChatCount: newUnreadChatCount });
        this.setChatListByType(type, updatedChatList);
    }
    getChatByChatLogId(chatLogId, groupType) {
        const chatList = this.getChatListByType(groupType);
        if (chatList) {
            // @ts-ignore
            return chatList.chats.find(chatData => chatData.chatLogId === chatLogId);
        }
    }
    deleteChat(groupType, chatId) {
        const chatList = this.getChatListByType(groupType);
        this.setChatListByType(groupType, Object.assign(Object.assign({}, chatList), { chats: chatList.chats.filter((chat) => chat.from !== chatId) }));
        return this.getChatListByType(groupType);
    }
    // Soft-delete: keeps the chat in the list with status=DELETED so the list
    // order stays stable (used by filtered chat lists).
    markChatAsDeleted(groupType, chatId) {
        const chatList = this.getChatListByType(groupType);
        this.setChatListByType(groupType, Object.assign(Object.assign({}, chatList), { chats: chatList.chats.map((chat) => chat.from === chatId
                ? Object.assign(Object.assign({}, chat), { status: models_1.CHAT_STATUS.DELETED }) : chat) }));
        return this.getChatListByType(groupType);
    }
    getChatByGroupType(groupType, chatId) {
        const chatList = this.getChatListByType(groupType);
        return chatList.chats.find((chat) => chat.from === chatId);
    }
    addChatToChatList(groupType, chat) {
        const chatList = this.getChatListByType(groupType);
        const updatedChatList = (0, utils_1.addChatByUpdatedAt)(chat, chatList);
        this.setChatListByType(groupType, Object.assign(Object.assign({}, updatedChatList), { chats: [...updatedChatList.chats] }));
        this.getChatListByType(groupType);
    }
}
exports.default = ChatsStorage;
